
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Transport } from '@/models/Transport'
import { namespace } from 'vuex-class'
import HopperMeasurementForm from '@/components/DocumentGenerators/HopperMeasurementForm.vue'
import { Vessel, VesselAttribute } from '@/models/Vessel'
import { Document } from '@/models/Document'
import axios from 'axios'
import { HopperMeasurement } from '@/models/HopperMeasurement'
import { HopperMeasurementsReport } from '@/models/HopperMeasurementsReport'
import FileTag from '@/enums/FileTag'
import snakecase from 'lodash.snakecase'

const Viewport = namespace('viewport')
const LOCAL_STORAGE_PREFIX = 'hopper-measurement-'

@Component({
	components: { HopperMeasurementForm }
})
export default class HopperMeasurementsReportGenerator extends Vue {
	@Viewport.Getter('isMobile')
	isMobile

	@Prop({ required: true })
	transport!: Transport

	hopperAmount: 'SINGLE' | 'DOUBLE' = 'DOUBLE'
	isLoading: boolean = false
	vesselId: string = ''

	// unfortunately Vue doesn't recalculate the totalVolume computed property when the
	// internal properties of the reference component change so a counter is introduced
	totalCounter: number = 0
	isDocumentGenerating: boolean = false
	vessel: Vessel | null = null
	bargingLetterDocuments: Array<Document> = []

	bargingLetterDocumentFront: Document | null = null
	bargingLetterDocumentRear: Document | null = null

	get hasBargingLetters(): boolean {
		return !!(
			this.vessel?.getVesselAttribute(snakecase(FileTag.BARGING_LETTER_FRONT)) ||
			this.vessel?.getVesselAttribute(snakecase(FileTag.BARGING_LETTER_REAR))
		)
	}

	get bargingLetterFrontDocuments(): Array<Document> {
		return this.bargingLetterDocuments.filter((doc) => doc.tag === FileTag.BARGING_LETTER_FRONT)
	}

	get bargingLetterRearDocuments(): Array<Document> {
		return this.bargingLetterDocuments.filter((doc) => doc.tag === FileTag.BARGING_LETTER_REAR)
	}

	get bargingLetterFront(): VesselAttribute | null {
		return (
			this.vessel?.getVesselAttributesBySlug(snakecase(FileTag.BARGING_LETTER_FRONT)).find((attr) => {
				const attributeValue = attr.value as {
					meta?: { document: { data: { id: string } } }
				}
				return `${attributeValue.meta?.document.data.id}` === `${this.bargingLetterDocumentFront?.getApiId()}`
			}) ?? null
		)
	}

	get bargingLetterRear(): VesselAttribute | null {
		return (
			this.vessel?.getVesselAttributesBySlug(snakecase(FileTag.BARGING_LETTER_REAR))?.find((attr) => {
				const attributeValue = attr.value as {
					meta?: { document: { data: { id: string } } }
				}
				return `${attributeValue.meta?.document.data.id}` === `${this.bargingLetterDocumentRear?.getApiId()}`
			}) ?? null
		)
	}

	get totalVolume(): number {
		// eslint-disable-next-line no-unused-expressions
		this.totalCounter // only mention it to trigger update when it changes
		const frontHopperForm = this.$refs.frontHopper as HopperMeasurementForm
		const rearHopperForm = this.$refs.rearHopper as HopperMeasurementForm
		let total = 0
		if (frontHopperForm) {
			total += frontHopperForm.calculatedVolume
		}
		if (rearHopperForm) {
			total += rearHopperForm.calculatedVolume
		}
		return total
	}

	get canSave(): boolean {
		return this.hasBargingLetters && this.bargingLetterDocuments.length > 0
	}

	get holdQuantity(): number | null {
		const attributeValue = this.vessel?.getVesselAttributesBySlug('hold_quantity')[0]
		return attributeValue?.value ? parseInt(attributeValue.value as string) : null
	}

	async created() {
		this.isLoading = true
		const vesselQuery = Vessel.option('include', 'vesselAttributes').find(
			this.transport.getActiveVessel().getApiId() as string
		)
		this.vessel = (await vesselQuery).getData() as Vessel
		this.vesselId = this.transport.getActiveVessel().getApiId() as string

		const documentIds = [
			...(this.vessel?.getVesselAttributesBySlug(snakecase(FileTag.BARGING_LETTER_FRONT)) ?? []),
			...(this.vessel?.getVesselAttributesBySlug(snakecase(FileTag.BARGING_LETTER_REAR)) ?? [])
		].map((attr) => {
			const attributeValue = attr.value as { meta?: { document: { data: { id: string } } } }
			return attributeValue.meta?.document.data.id
		})

		if (documentIds.length) {
			const documentsQuery = Document.where('id', documentIds.join(',')).get()
			this.bargingLetterDocuments = (await documentsQuery).getData() as Array<Document>
		}

		this.isLoading = false

		this.setStateFromVesselAttributes()
		this.setStateFromLocalStorage()
		this.preselectBargingLetters()
	}

	preselectBargingLetters() {
		if (!this.bargingLetterDocumentFront && this.bargingLetterFrontDocuments.length === 1) {
			this.bargingLetterDocumentFront = this.bargingLetterFrontDocuments[0]
		}
		if (!this.bargingLetterDocumentRear && this.bargingLetterRearDocuments.length === 1) {
			this.bargingLetterDocumentRear = this.bargingLetterRearDocuments[0]
		}
	}

	setStateFromVesselAttributes() {
		if (this.holdQuantity) {
			this.hopperAmount = this.holdQuantity > 1 ? 'DOUBLE' : 'SINGLE'
		}
	}

	setStateFromLocalStorage() {
		let localStoragedData = {} as {
			hopperAmount?: 'SINGLE' | 'DOUBLE'
			chosenFrontBargingLetterId?: string
			chosenRearBargingLetterId?: string
		}
		try {
			localStoragedData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_PREFIX + this.vesselId) ?? '{}')
			if (localStoragedData?.hopperAmount) {
				this.hopperAmount = localStoragedData.hopperAmount
			}
		} catch {
			return
		}

		if (localStoragedData.chosenFrontBargingLetterId) {
			this.bargingLetterDocumentFront =
				this.bargingLetterDocuments.find(
					(d) => d.getApiId() === localStoragedData.chosenFrontBargingLetterId
				) ?? null
		}

		if (localStoragedData.chosenRearBargingLetterId) {
			this.bargingLetterDocumentRear =
				this.bargingLetterDocuments.find((d) => d.getApiId() === localStoragedData.chosenRearBargingLetterId) ??
				null
		}
	}

	closeDocumentCreator({ generateUnloadingBill = false }: { generateUnloadingBill?: boolean } = {}) {
		if (generateUnloadingBill) {
			this.$emit('generate-loading-bill')

			return
		}

		this.$emit('creator-closed')
	}

	private fillMeasurement(measurement: HopperMeasurement, form: HopperMeasurementForm) {
		measurement.measurements = {
			left: [form.measurementLeft1, form.measurementLeft2, form.measurementLeft3, form.measurementLeft4].map(
				(val) => {
					if (val === null) return val
					return Number.parseInt(val)
				}
			),
			right: [form.measurementRight1, form.measurementRight2, form.measurementRight3, form.measurementRight4].map(
				(val) => {
					if (val === null) return val
					return Number.parseInt(val)
				}
			)
		}
	}

	buildRequestBody() {
		const report = new HopperMeasurementsReport()
		report.transshipmentDirection = 'unloading'

		const frontHopperForm = this.$refs.frontHopper as HopperMeasurementForm
		const rearHopperForm = this.$refs.rearHopper as HopperMeasurementForm

		const measurements: Array<HopperMeasurement> = []
		if (frontHopperForm) {
			const frontHopperMeasurement = new HopperMeasurement()
			frontHopperMeasurement.setBargingLetter(this.bargingLetterDocumentFront)
			frontHopperMeasurement.setApiId('client-side-generated-front-hopper')
			this.fillMeasurement(frontHopperMeasurement, frontHopperForm)
			measurements.push(frontHopperMeasurement)
		}
		if (rearHopperForm) {
			const rearHopperMeasurement = new HopperMeasurement()
			rearHopperMeasurement.setBargingLetter(this.bargingLetterDocumentRear)
			rearHopperMeasurement.setApiId('client-side-generated-rear-hopper')
			this.fillMeasurement(rearHopperMeasurement, rearHopperForm)
			measurements.push(rearHopperMeasurement)
		}
		report.setMeasurements(measurements)

		return {
			// @ts-ignore
			...report.serialize(),
			// @ts-ignore
			included: measurements.map((measurement) => measurement.serialize())
		}
	}

	async generateDocument({ generateUnloadingBill = false }: { generateUnloadingBill?: boolean } = {}) {
		const valid = await this.$validator.validateAll()
		if (!valid) return

		this.isDocumentGenerating = true

		const requestBody = this.buildRequestBody()

		const hopperMeasurementByVesselId = {
			hopperAmount: this.hopperAmount,
			chosenFrontBargingLetterId: this.bargingLetterDocumentFront?.getApiId(),
			chosenRearBargingLetterId: this.bargingLetterDocumentRear?.getApiId()
		}

		this.vesselId = this.transport.getActiveVessel().getApiId() as string

		try {
			await axios.post(`${this.transport.getModelUrl()}/hopperMeasurements`, requestBody)
			window.localStorage.setItem(
				LOCAL_STORAGE_PREFIX + this.vesselId,
				JSON.stringify(hopperMeasurementByVesselId)
			)

			this.closeDocumentCreator({ generateUnloadingBill })
		} finally {
			this.isDocumentGenerating = false
		}
	}
}
